import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { setApiViewMode } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/* Owner View Mode
 *
 * Owner accounts (`user.is_owner === true`) can toggle between:
 *   - "owner": every API call goes without the X-View-Mode header, the
 *     backend recognises the user as an owner and applies bypass logic
 *     (auto-unlock new projects, skip Stripe checkout, HD downloads, etc.)
 *   - "customer": every API call is sent with `X-View-Mode: customer`. The
 *     backend treats the request as if it came from a normal paying
 *     customer (no bypass) so the owner can experience and verify the
 *     exact customer flow without touching the database or logging out.
 *
 * Persisted in localStorage under `cm_view_mode` so a refresh keeps the
 * chosen mode. Non-owner users never see the toggle; their requests
 * never carry the header. */

const ViewModeContext = createContext(null);
const STORAGE_KEY = "cm_view_mode";

export function ViewModeProvider({ children }) {
  const { user } = useAuth();
  const isOwner = !!(user && user !== false && user.is_owner);

  const [viewMode, setViewModeState] = useState(() => {
    if (typeof window === "undefined") return "owner";
    return window.localStorage.getItem(STORAGE_KEY) || "owner";
  });

  // Push the chosen mode down to the axios interceptor so every request
  // (including the ones fired from within deeply nested components) carries
  // the correct header.
  useEffect(() => {
    setApiViewMode(viewMode);
  }, [viewMode]);

  // If the current user is not an owner, force mode back to owner (default)
  // so that the header never fires on accounts that don't need it.
  useEffect(() => {
    if (!isOwner && viewMode !== "owner") {
      setViewModeState("owner");
      try { window.localStorage.setItem(STORAGE_KEY, "owner"); } catch { /* ignore */ }
    }
  }, [isOwner, viewMode]);

  const setViewMode = useCallback((next) => {
    const normalised = next === "customer" ? "customer" : "owner";
    setViewModeState(normalised);
    try { window.localStorage.setItem(STORAGE_KEY, normalised); } catch { /* ignore */ }
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(viewMode === "customer" ? "owner" : "customer");
  }, [viewMode, setViewMode]);

  return (
    <ViewModeContext.Provider value={{ viewMode, isOwner, setViewMode, toggleViewMode }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export const useViewMode = () => useContext(ViewModeContext) || { viewMode: "owner", isOwner: false, setViewMode: () => {}, toggleViewMode: () => {} };
